/**
 * RepeatIncidentsWidget — where the same thing keeps happening.
 *
 * Recurrence is judged on MEANINGFUL identifiers (location, incident type, root
 * cause, equipment, department, area) — never on identical description text,
 * which finds copy-paste rather than recurrence. The grouping is the reader's
 * choice; the default is location + incident type.
 *
 * A count alone does not answer the question management is really asking, so
 * each group also carries whether the earlier ones were CLOSED and whether a
 * CAPA was ever raised. A group with five occurrences, all closed, and no CAPA
 * is the actionable finding: the closures did not prevent recurrence.
 *
 * Rendered as a table rather than a chart: a repeat group has five numbers that
 * matter, and bar length can only carry one of them.
 */
import React, { useState } from 'react';
import { Repeat, ShieldCheck, ChevronRight } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchRepeatIncidents } from '../api/client';
import DrilldownModal from './DrilldownModal';
import { CARD, HEADING } from '../ui';

const DEFAULT_KEYS = ['location', 'incident_category'];

// Selectable groupings. Kept in step with analytics_safety.REPEAT_KEYS, and the
// server rejects anything outside its own whitelist regardless.
const GROUPINGS = [
  { id: 'location,incident_category', label: 'Location + Type', keys: ['location', 'incident_category'] },
  { id: 'location', label: 'Location', keys: ['location'] },
  { id: 'incident_category', label: 'Incident Type', keys: ['incident_category'] },
  { id: 'cause', label: 'Root Cause', keys: ['cause'] },
  { id: 'equipment', label: 'Equipment', keys: ['equipment'] },
  { id: 'department', label: 'Department', keys: ['department'] },
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export default function RepeatIncidentsWidget({
  title = 'Repeat Incidents', minCount = 2, limit = 12,
}) {
  const { siteId, time, departmentId, dimensions, fyStartMonth, refreshMs } = useDashboard();
  const [grouping, setGrouping] = useState(GROUPINGS[0].id);
  const [drill, setDrill] = useState(null);

  const keys = (GROUPINGS.find((g) => g.id === grouping) || GROUPINGS[0]).keys || DEFAULT_KEYS;
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { group_keys: keys, min_count: minCount, limit };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (time) body.time = time;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      return fetchRepeatIncidents(body, { signal });
    },
    [siteId, timeKey, departmentId, dimsKey, keys.join(','), minCount, limit, fyStartMonth],
    { refreshMs },
  );

  const groups = data?.groups || [];
  const isEmpty = !loading && !error && groups.length === 0;

  return (
    <>
      <div className={`${CARD} p-4`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className={`${HEADING} flex items-center gap-2`}>
            <Repeat className="h-4 w-4 text-orange-500" /> {title}
          </h3>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Group by
            <select
              value={grouping}
              onChange={(e) => setGrouping(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800
                         focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {GROUPINGS.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </label>
        </div>

        {loading && <div className="h-48 animate-pulse rounded bg-gray-100" />}
        {!loading && error && (
          <div className="py-10 text-center text-sm text-red-600">
            Could not load repeat incidents.
          </div>
        )}
        {isEmpty && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            No recurrence on this grouping — nothing occurred {data?.min_count ?? minCount} or
            more times in the selected period.
          </div>
        )}

        {!loading && !error && groups.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="py-2 pr-3 font-semibold">Group</th>
                  <th className="py-2 px-2 text-right font-semibold">Times</th>
                  <th className="py-2 px-2 text-right font-semibold">Still open</th>
                  <th className="py-2 px-2 text-right font-semibold">With CAPA</th>
                  <th className="py-2 px-2 font-semibold">Last</th>
                  <th className="py-2 pl-2" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  // All closed, none with a CAPA, still recurring — the case
                  // where "we fixed it" demonstrably did not hold.
                  const unaddressed = g.with_capa === 0;
                  return (
                    <tr
                      key={g.label}
                      onClick={() => setDrill(g)}
                      className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-blue-50/60"
                      title="Show the incidents in this group"
                    >
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-gray-800">{g.label}</span>
                          <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700">
                            Repeat
                          </span>
                          {unaddressed && (
                            <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">
                              No CAPA
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-500">
                          {fmtDate(g.first_date)} → {fmtDate(g.last_date)}
                          {g.high_severity > 0 && (
                            <> · {g.high_severity} high severity</>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums text-gray-900">
                        {g.occurrences}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-gray-600">
                        {g.open_count}
                      </td>
                      <td className={`py-2 px-2 text-right tabular-nums ${unaddressed ? 'text-red-600' : 'text-gray-600'}`}>
                        {g.with_capa}
                      </td>
                      <td className="py-2 px-2 whitespace-nowrap text-gray-600">{fmtDate(g.last_date)}</td>
                      <td className="py-2 pl-2 text-gray-300">
                        <ChevronRight className="h-4 w-4" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
              A group is flagged as a repeat when the same{' '}
              {keys.map((k) => (GROUPINGS.find((g) => g.keys.length === 1 && g.keys[0] === k)?.label || k)).join(' + ').toLowerCase()}{' '}
              occurs {data?.min_count ?? minCount} or more times in the selected period.
              &ldquo;With CAPA&rdquo; counts occurrences linked to a corrective action —
              recurrence with no CAPA is where closure did not prevent a repeat.
              {data?.excludes_untagged && ' Grouping by root cause only covers incidents that have a cause category recorded.'}
            </p>
          </div>
        )}
      </div>

      <DrilldownModal
        open={!!drill}
        metric={data?.drill?.metric || 'safety.incidents.total'}
        title={`Repeat incidents — ${drill?.label ?? ''}`}
        subtitle="Every occurrence in this group, newest first. Open one to see its cause, closure and corrective action."
        extraDimensions={drill?.filters || null}
        onClose={() => setDrill(null)}
      />
    </>
  );
}
